import { clsx } from "clsx";
const headingFonts = {
  Omnes: "WoltHeading-Omnes",
  October: "WoltHeading-October",
  MPlus: "WoltHeading-M-Plus-Rounded-1c"
};
const DATA_ATTR_THEME = "data-cb-theme";
const DATA_ATTR_HEADING_FONT = "data-cb-heading-font";
const GLOBAL_PORTAL_ELEMENT_ID = "cb-portal";
const unsafeObjectKeys = (object) => Object.keys(object);
const unsafeObjectFromEntries = (pairs) => Object.fromEntries(pairs);
const unsafeObjectEntries = (object) => Object.entries(object);
const BASE_FONT_SIZE = 16;
const ELEVATED_CONTAINER_CLASSNAME = "cb-elevated";
const toKebabCase = (str) => str.replace(/([a-z])([A-Z])/g, "$1-$2").replace(/^-+|-+$/g, "").toLowerCase();
function pxToRem(px) {
  return `${px / BASE_FONT_SIZE}rem`;
}
function keyToCssVarName(key, options) {
  const keyName = (options == null ? void 0 : options.prefix) ? `${options.prefix}-${key}` : key;
  return `--${(options == null ? void 0 : options.kebabCase) ? toKebabCase(keyName) : keyName}`;
}
function createCssVarDefinitions(varMap, options) {
  return unsafeObjectEntries(varMap).map(([key, value]) => `${keyToCssVarName(key, options)}: ${value};`).join("\n");
}
function createCssVars(varConfig, options) {
  const varMap = Object.fromEntries(varConfig.map((key) => {
    const val = `var(${keyToCssVarName(key, options)})`;
    return [key, (options == null ? void 0 : options.kebabCase) ? toKebabCase(val) : val];
  }));
  Object.freeze(varMap);
  return varMap;
}
function createThemedCssVarDefinitions(varMap, options) {
  const {
    kebabCase,
    prefix,
    selector = "&"
  } = options ?? {};
  const {
    darkModeElevatedVars,
    darkModeVars,
    lightHighContrastVars,
    unscopedVars
  } = unsafeObjectEntries(varMap).reduce((acc, [key, value]) => {
    if (typeof value === "string") {
      acc.unscopedVars[key] = value;
      return acc;
    }
    acc.unscopedVars[key] = value.light;
    if (value.lightHighContrast && value.lightHighContrast !== value.light) {
      acc.lightHighContrastVars[key] = value.lightHighContrast;
    }
    if (typeof value.dark === "object") {
      if (value.dark.default !== value.light) {
        acc.darkModeVars[key] = value.dark.default;
      }
      acc.darkModeElevatedVars[key] = value.dark.elevated;
    } else if (typeof value.dark === "string" && value.dark !== value.light) {
      acc.darkModeVars[key] = value.dark;
    }
    return acc;
  }, {
    unscopedVars: {},
    lightHighContrastVars: {},
    darkModeVars: {},
    darkModeElevatedVars: {}
  });
  const unscopedCss = createCssVarDefinitions(unscopedVars, {
    prefix,
    kebabCase
  });
  const lightHighContrastCss = createCssVarDefinitions(lightHighContrastVars, {
    prefix,
    kebabCase
  });
  const darkCss = createCssVarDefinitions(darkModeVars, {
    prefix,
    kebabCase
  });
  const darkElevatedCss = createCssVarDefinitions(darkModeElevatedVars, {
    prefix,
    kebabCase
  });
  const rootSelector = selector === ":root" ? ":root" : "html";
  return [selector === ":root" && unscopedCss && `:root{${unscopedCss}}`, selector !== ":root" && unscopedCss, !!lightHighContrastCss && `${rootSelector}:where([${DATA_ATTR_THEME}="lightHighContrast"]) ${selector === ":root" ? "" : selector} {${lightHighContrastCss}}`, !!darkCss && `${rootSelector}:where([${DATA_ATTR_THEME}="dark"]) ${selector === ":root" ? "" : selector} {${darkCss}}`, !!darkElevatedCss && `${rootSelector}:where([${DATA_ATTR_THEME}="dark"]) :where(.${ELEVATED_CONTAINER_CLASSNAME}) ${selector === ":root" ? "" : selector} {${darkElevatedCss}}`].filter(Boolean).join("\n\n");
}
function createAttributeCssVarDefinitions(attrName, params, options) {
  return unsafeObjectEntries(params.values).map(([key, val]) => {
    var _a;
    return `&[${attrName}='${key}'] {
          ${createThemedCssVarDefinitions(val, options)}
          ${((_a = params.extraCss) == null ? void 0 : _a[key]) ?? ""}
        }`;
  }).join("\n");
}
function createThemedCssVarsAndDefinitions(varMap, options) {
  return {
    vars: createCssVars(unsafeObjectKeys(varMap), options),
    definitions: createThemedCssVarDefinitions(varMap, options)
  };
}
function themedConfigToThemedAttrs(variantConfig) {
  return unsafeObjectFromEntries(unsafeObjectKeys(variantConfig.light).map((key) => {
    var _a;
    return [key, {
      light: variantConfig.light[key],
      lightHighContrast: ((_a = variantConfig.lightHighContrast) == null ? void 0 : _a[key]) ?? variantConfig.light[key],
      dark: variantConfig.dark[key]
    }];
  }));
}
function assignInlineCssVars(_variables, values) {
  const cssVars = {};
  for (const prop in values) {
    cssVars[`--${prop}`] = values[prop];
  }
  return cssVars;
}
const NEW_ALCHEMY_GLOBAL_PORTAL_ELEMENT_ID = "alchemy-portal";
const globalMountElements = {};
const canUseDOM = !(typeof window !== "object" || !document);
const getPortalElement = (id) => {
  if (!canUseDOM) return null;
  const existingElement = globalMountElements[String(id)];
  if (existingElement) {
    if (!existingElement.style.position) {
      existingElement.style.position = "fixed";
      existingElement.style.insetInline = "0";
      existingElement.style.top = "0";
    }
    return existingElement;
  }
  const mountElement = id === void 0 ? document.querySelector(`#${GLOBAL_PORTAL_ELEMENT_ID}, #${NEW_ALCHEMY_GLOBAL_PORTAL_ELEMENT_ID}`) : document.querySelector(`#${id}`);
  if (mountElement) {
    globalMountElements[String(id)] = mountElement;
    return mountElement;
  }
  const newMountElement = document.createElement("div");
  newMountElement.id = id ?? GLOBAL_PORTAL_ELEMENT_ID;
  newMountElement.setAttribute("role", "region");
  newMountElement.style.position = "fixed";
  newMountElement.style.insetInline = "0";
  newMountElement.style.top = "0";
  document.body.append(newMountElement);
  globalMountElements[String(id)] = newMountElement;
  return newMountElement;
};
function parseLanguageFromLocale(locale) {
  const regex = /^[a-z]{2}/i;
  const match = locale.match(regex);
  if (!match) {
    throw new Error(`Unable to parse language from locale: '${locale}'.`);
  }
  return match[0].toLowerCase();
}
function resolveHeadingFontByLocale(locale) {
  const language = parseLanguageFromLocale(locale);
  if (language === "ja") {
    return "WoltHeading-M-Plus-Rounded-1c";
  }
  if (["kk", "ka", "el", "he"].includes(language)) {
    return "WoltHeading-October";
  }
  return "WoltHeading-Omnes";
}
const rootPaths = {
  development: "https://webfonts.development.dev.woltapi.com",
  production: "https://webfonts.wolt.com"
};
const fontFacePaths = {
  "m-plus-rounded-1c": ["M-PLUS-Rounded-1c/m-plus-rounded-1c-v10-latin_japanese-700", "M-PLUS-Rounded-1c/m-plus-rounded-1c-v10-latin_japanese-900"],
  OctoberCyrillic: ["October/OctoberCyrillicKazakh-Bold", "October/OctoberCyrillicKazakh-Black"],
  OctoberGeorgian: ["October/OctoberGeorgian-Bold", "October/OctoberGeorgian-Black"],
  OctoberGreek: ["October/OctoberGreek-Bold", "October/OctoberGreek-Black"],
  OctoberHebrew: ["October/OctoberHebrew-Bold", "October/OctoberHebrew-Black"],
  OctoberLatin: ["October/OctoberLatin-Bold", "October/OctoberLatin-Black"],
  OmnesArabic: ["Omnes/OmnesArabic-SemiBold", "Omnes/OmnesArabic-Bold"],
  OmnesCyrillic: ["Omnes/OmnesCyrillic-SemiBold", "Omnes/OmnesCyrillic-Bold"],
  OmnesLatin: ["Omnes/OmnesLatin-SemiBold", "Omnes/OmnesLatin-Bold"]
};
const fontFacesByLanguage = {
  ar: ["OmnesArabic"],
  el: ["OctoberGreek", "OctoberLatin"],
  he: ["OctoberHebrew", "OctoberLatin"],
  ja: ["m-plus-rounded-1c"],
  ka: ["OctoberGeorgian", "OctoberLatin"],
  kk: ["OctoberCyrillic", "OctoberLatin"],
  ru: ["OmnesCyrillic"],
  uk: ["OmnesCyrillic"]
};
const FALLBACK_FONT_FACES = ["OmnesLatin"];
const getFontFacePath = (env, fontFilePath) => `${rootPaths[env]}/fonts/${fontFilePath}.woff2`;
function getPreloadFontFileUrlsByLocale(locale, env) {
  const language = parseLanguageFromLocale(locale);
  return (fontFacesByLanguage[language] ?? FALLBACK_FONT_FACES).flatMap((fontFace) => fontFacePaths[fontFace]).map((path) => getFontFacePath(env, path));
}
const getDocumentAttributes = (params) => ({
  [DATA_ATTR_THEME]: params.themeMode ?? "light",
  [DATA_ATTR_HEADING_FONT]: resolveHeadingFontByLocale(params.locale),
  dir: params.direction
});
function objToHtmlElementAttrs(attrs) {
  return Object.entries(attrs).map(([key, val]) => val === void 0 ? key : `${key}="${val}"`).join(" ");
}
export {
  DATA_ATTR_HEADING_FONT,
  DATA_ATTR_THEME,
  GLOBAL_PORTAL_ELEMENT_ID,
  assignInlineCssVars,
  clsx,
  createAttributeCssVarDefinitions,
  createCssVarDefinitions,
  createCssVars,
  createThemedCssVarDefinitions,
  createThemedCssVarsAndDefinitions,
  getDocumentAttributes,
  getPortalElement,
  getPreloadFontFileUrlsByLocale,
  headingFonts,
  objToHtmlElementAttrs,
  parseLanguageFromLocale,
  pxToRem,
  resolveHeadingFontByLocale,
  themedConfigToThemedAttrs,
  unsafeObjectEntries,
  unsafeObjectFromEntries,
  unsafeObjectKeys
};
//# sourceMappingURL=index.js.map
